import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import useFolders from '@hooks/use-folders/useFolders.tsx';
import { findValidationMessage } from '@utils/validation';
import InputFolders from '@components/framework/input/InputFolders/InputFolders.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue';
import {
  CompanyLocationRampWithOpeningHoursOutDTO,
  CompanyModuleEnum,
  ComputeReservationDurationConfiguration,
  ExpeditionDetailV3OutDTO,
  ExternalOrdersApiTypeEnum,
  GetExternalOrdersResponse,
  FeaturesEnum,
  FlagSetting,
  FreeSlotsWrapperOutDTO,
  LocationPreviewForCheckExpResponseOutDTO,
  LovCompanyCodeListDTO,
  MessageResponse,
  ReservationCoordinates,
  ReservationFlagTypeEnum,
  TimeslotCalendarDTO,
  EntityTypeEnum,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import { isNotNullOrUndefined, isNullOrUndefined, parseNumberOrUndefined } from '@utils/utils';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import ReservationCheckResult from '@components/ringil3/Features/Reservation/Helper/ReservationCheckResult';
import { InputText } from 'primereact/inputtext';
import RingilTextArea from '@components/framework/input/RingilTextArea';
import styled from 'styled-components';
import { ColWithGap, RowWithGap, RowWithSpaceBetween } from '@components/styles';
import { ClickableCursor, ColOdd, HorizontalLine, TwoColGrid } from '@components/ringil3/styles';
import InputManipulationType from '@components/ringil3/Input/InputManipulationType/InputManipulationType';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix';
import InputNoteWithLink from '@components/ringil3/Input/InputNoteWithLink/InputNoteWithLink';
import InputEntityOwner from '@components/obsolete/Form/InputEntityOwner/InputEntityOwner';
import InputCompanyLocation from '@components/obsolete/Form/InputEntityOwner/InputCompanyLocation';
import ValidationDiv from '@utils/validation';
import { findErrorMessage } from '@utils/validationUtils.ts';
import { CreateTimeslotForm } from '@components/ringil3/Features/Reservation/Sidedbars/types.ts';
import {
  buildCreateTimeslotValidationSchema,
  linkedExpeditionWithoutOrdersMessage,
} from '@components/ringil3/Features/Reservation/Sidedbars/createTimeslotValidation.ts';
import ErrorCountBadge from '@components/ringil3/Badges/ErrorBadge';
import InputCompanyRamp from '@components/ringil3/Input/InputCompanyRamp/InputCompanyRamp';
import { useDebounce } from 'use-debounce';
import InputHourManual from '@components/ringil3/Input/InputHour/InputHourManual';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage';
import dayjs from 'dayjs';
import {
  dayjsFromApiUtcInTz,
  stringHHmmFromDayjs,
  stringHHmmFromStringInTz,
} from '@utils/dateWithTz.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon';
import { ProgressSpinner } from 'primereact/progressspinner';
import { emptyOrder, isExternalIdPresent } from '@components/framework/orders/common.tsx';
import SvgAvailableDates from '@components/ringil3/icons/reservation/AvailableDates';
import SvgRefresh from '@components/ringil3/icons/Refresh';
import Panel from '@components/ringil3/Panel/Panel';
import SvgClose from '@components/ringil3/icons/panel/Close';
import COSelect from '@components/ringil3/COSelect/COSelect';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import OrdersComponent from '@components/framework/orders/OrdersComponent.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import InputCamera from '@components/framework/input/InputCamera/InputCamera.tsx';
import { generateTimeslotOrderConfig } from '@components/ringil3/Features/Reservation/orderConfig.ts';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import SvgWarningExclamation from '@components/ringil3/icons/WarningExclamation.tsx';
import SvgForExpedition from '@icons/ForExpedition.tsx';
import InputInheriterCompanyRES from '@components/obsolete/Form/InputEntityOwner/InputInheriterCompanyRES.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { useLovTruckTypes } from '@hooks/customer.hooks.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import Tabs from '@components/ringil3/RingilTabs/Tabs.tsx';
import ReservationFlagsField from '@components/RingilComponents/form/ReservationFlagsField';
import { toEditableFlags } from '@components/RingilComponents/form/flagUnitsLimit';
import { toTestIdSlug } from '@utils/testUtils';
import { formatCompanyCodeListLabel } from '@utils/formators';
import { toCompactTimePart } from '@utils/carrierShipmentTestIds';
import RingilCalendar from '@components/framework/input/RingilCalendar.tsx';
import { useExternalOrdersDateRange } from '@components/ringil3/Features/Reservation/Helper/useExternalOrdersDateRange';
import { shouldDoubleDuration } from '@components/ringil3/Features/Reservation/Helper/shouldDoubleDuration';
import { defaultActionTypes } from '@components/ringil3/Features/Reservation/Helper/defaultActionTypes';
import {
  buildCreateReservationRequest,
  checkOrderMismatch,
  createTimeslotInitialData,
  expeditionOrdersForReservation,
  mapGlobalOrderWithItemsForCheckTimeslots,
  timeslotForReservationCheck,
} from '@components/ringil3/Features/Reservation/Sidedbars/createTimeslotMapping.ts';
import NoAvailableSlotsMessage from '@components/RingilComponents/NoAvailableSlots/NoAvailableSlotsMessage.tsx';

interface ComponentProps {
  onContinue: (reservation: ReservationCoordinates) => void;
  initialData?: CreateTimeslotForm;
  headerDisabled?: boolean;
  showCamera?: boolean;
  showOpenTheGateCheckbox?: boolean;
  showOrders?: boolean;
  showNameAndNote?: boolean;
}

export interface CreateTimeslotRequest {
  applicationId: string;
  expeditionId: string;
  companyLocationId: string;
  companyLocationCustomerId: string;
  expeditionLocationId?: string;
  preferredDate: string;
  cameraId?: string;
  carPlate?: string;
  cameraLogId?: string;
  reservationId?: string;
  rampId?: string;
  arrivalHourString?: string;
  timezoneId?: string;
  testIdPrefix?: string;
}

export const useCreateTimeslotSidebar = () => {
  const [createTimeslotRequest, setCreateTimeslotRequest] = useState<CreateTimeslotRequest>(null);

  const CreateTimeslotSidebar: React.FC<ComponentProps> = ({
    onContinue,
    initialData,
    showCamera = false,
    showOrders = true,
    showNameAndNote = true,
    headerDisabled = true,
  }) => {

    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    // RD-2977: folders the user may file into (LOV is already access-filtered)
    const accessibleFolders = useFolders(EntityTypeEnum.TIMESLOT);
    const {
      getCreateEditReservationSettings,
      checkReservation,
      createReservation,
      getFreeSlotsForLocationReservation,
      computeReservationDurationWithManipulationUnits,
      getExpeditionV3Detail,
      checkReservationIfExpeditionCanBeCreated,
      getSharedRampsForCompanyLocation,
      getExternalOrders,
      lovOnBehalfOfForInheritor,
      lovAllCompaniesByInheritModule,
    } = useContext(ApiContext);
    const [slots, setSlots] = useState<FreeSlotsWrapperOutDTO[]>();
    const [settings, setSettings] = useState<ComputeReservationDurationConfiguration>();
    const [ramps, setRamps] = useState<CompanyLocationRampWithOpeningHoursOutDTO[]>([]);
    const [isReservationOrderLinkedToExpOrder, setIsReservationOrderLinkedToExpOrder] = useState(false);
    const [linkedExpeditionWithoutOrders, setLinkedExpeditionWithoutOrders] = useState(false);
    const [expeditionLocations, setExpeditionLocations] = useState<LocationPreviewForCheckExpResponseOutDTO[]>([]);
    const [canCreateLinkedExpedition, setCanCreateLinkedExpedition] = useState(false);
    const [linkedExpeditionError, setLinkedExpeditionError] = useState<string>(null);
    const [expeditionApplicationId, setExpeditionApplicationId] = useState<string>(null);
    const [truckTypeEnabled, setTruckTypeEnabled] = useState(false);
    const [truckTypeRequired, setTruckTypeRequired] = useState(false);
    const [loading, setLoading] = useState(false);
    const [requiredOrderItems, setRequiredOrderItems] = useState<boolean>(false);
    const truckTypes = useLovTruckTypes();
    const [activeTab, setActiveTab] = useState(0);
    const [externalOrdersResponse, setExternalOrdersResponse] = useState<GetExternalOrdersResponse | undefined>(undefined);
    const onBehalfOfEnabled = settings?.onBehalfOfEnabled === true;
    const [onBehalfOfOptions, setOnBehalfOfOptions] = useState<LovCompanyCodeListDTO[]>([]);

    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();

    const flagsConfig = (settings?.flags ?? {}) as Partial<Record<ReservationFlagTypeEnum, FlagSetting>>;

    const schema = buildCreateTimeslotValidationSchema({
      tr,
      settings,
      loggedUserCompanyId: loggedUser?.companyId,
      truckTypeRequired,
      showOrders,
      isReservationOrderLinkedToExpOrder,
      linkedExpeditionWithoutOrders,
      externalOrdersResponse,
    });

    const createInitialData = (
      companyLocationId: string,
      companyLocationCustomerId: string,
      preferredDate?: dayjs.Dayjs,
      cameraId?: string,
      carPlate?: string,
      rampId?: string,
      arrivalHourString?: string,
      timezoneId?: string,
    ): CreateTimeslotForm =>
      createTimeslotInitialData(
        { companyLocationId, companyLocationCustomerId, preferredDate, cameraId, carPlate, rampId, arrivalHourString, timezoneId },
        showOrders,
        requiredOrderItems,
      );

    const form = useForm<CreateTimeslotForm>(
      schema,
      initialData ||
        createInitialData(
          createTimeslotRequest.companyLocationId,
          createTimeslotRequest.companyLocationCustomerId,
          createTimeslotRequest.preferredDate ? dayjs.utc(createTimeslotRequest.preferredDate) : null,
          createTimeslotRequest.cameraId,
          createTimeslotRequest.carPlate,
          createTimeslotRequest.rampId,
          createTimeslotRequest.arrivalHourString,
          createTimeslotRequest.timezoneId,
        ),
      d => save(d),
      false,
      false,
      null,
      'scroll',
    );

    // async API callbacks capture the values from the render they were created in, so read the current ones through a ref
    const valuesRef = useRef(form.values);
    valuesRef.current = form.values;
    // the customer the settings were last applied for - a repeated response for the same customer must not reset the timeslot
    const settingsCustomerIdRef = useRef<string>(null);

    const [reservationValid, setReservationValid] = useState(undefined);
    const [reservationCheckMessages, setReservationCheckMessages] = useState<MessageResponse[]>([]);
    const [stateForDeffered, setStateForDeffered] = useState(undefined);
    const [debouncedValue] = useDebounce(stateForDeffered, 700);

     useEffect(() => {
       const ab = new AbortController();

       const { rampId, durationInMin, arrivalHourString, localDate, timezoneId } = form.values.timeslot;

       if (rampId && durationInMin && arrivalHourString && localDate && timezoneId) {
         setStateForDeffered({
           rampId,
           durationInMin,
           arrivalHourString,
           localDate,
           timezoneId,
         });
       }

       setReservationValid(undefined);
       return () => {
         ab.abort();
       };
     }, [form.values.timeslot, form.values.timeslot?.arrivalHourString, form.values.timeslot?.durationInMin, form.values.timeslot?.rampId, form.values.timeslot?.localDate, form.values.timeslot?.timezoneId]);

    // wait 700ms after use change this object
    const [changedOrders] = useDebounce(form.values.orders, 700);

    // when orders, action types or the doubling flag change, recompute durationInMinutes
    useEffect(() => {
      const ac = new AbortController();

      recomputeMinDuration(ac);

      return () => {
        ac.abort();
      };
    }, [changedOrders, form.values.actionTypes, settings?.doubleLoadingUnloadingTime]);

    useEffect(() => {
      if (isNotNullOrUndefined(form?.values?.companyLocationCustomerId)) {
        const customerId = form.values.companyLocationCustomerId;
        getCreateEditReservationSettings(customerId, d => {
          setSettings(d);
          const isCustomerChange = isNotNullOrUndefined(settingsCustomerIdRef.current) && settingsCustomerIdRef.current !== customerId;
          settingsCustomerIdRef.current = customerId;

          const current = valuesRef.current;
          const hasOrders = isNotNullOrUndefined(current.orders) && current.orders.length > 0;

          form.setValues({
            ...(isNullOrUndefined(initialData) ? { actionTypes: defaultActionTypes(d, isOwner) } : {}),
            // never write back the orders captured in this closure - the expedition prefill may have replaced them meanwhile
            ...(showOrders === true && !hasOrders
              ? { orders: [emptyOrder(isOwner ? d?.ownerManipulationItemsRequired : d?.counterPartyManipulationItemsRequired)] }
              : {}),
            timeslot: !isCustomerChange
              ? // keep what the caller prefilled (and what the ramp/timezone effects resolved), only apply the default duration
                { ...current.timeslot, durationInMin: d.defaultDuration.toString() }
              : // the customer changed, so the previously picked ramp/slot no longer applies
                {
                  durationInMin: d.defaultDuration.toString(),
                  arrivalHourString: createTimeslotRequest?.arrivalHourString ?? null,
                  localDate: createTimeslotRequest.preferredDate ? dayjs.utc(createTimeslotRequest.preferredDate).format('YYYY-MM-DD') : dayjs.utc().format('YYYY-MM-DD'),
                  rampId: createTimeslotRequest?.rampId ?? null,
                  timezoneId: createTimeslotRequest.timezoneId,
                },
          });
        });
      }
    }, [form?.values?.companyLocationCustomerId]);

    useEffect(() => {
      if (!onBehalfOfEnabled || !isNotNullOrUndefined(form?.values?.companyLocationCustomerId)) {
        setOnBehalfOfOptions([]);
        return;
      }
      lovOnBehalfOfForInheritor(form.values.companyLocationCustomerId, d => setOnBehalfOfOptions(d));
    }, [onBehalfOfEnabled, form?.values?.companyLocationCustomerId]);

    const recomputeMinDuration = (ac: AbortController) => {
      if (isNullOrUndefined(form.values.orders) || isNullOrUndefined(form.values.companyLocationCustomerId)) return;

      computeReservationDurationWithManipulationUnits(
        form.values.companyLocationCustomerId,
        {
          items: form.values.orders
            .flatMap(o => o.items)
            .filter(t => t.cargoTemplateCode !== null)
            .map(i => ({ cargoItemType: i.cargoTemplateCode, quantity: i.amount })),
        },
        d => {
          if (ac && ac.signal.aborted) return;
          const multiplier = shouldDoubleDuration(settings, form.values.actionTypes) ? 2 : 1;
          form.setFieldValue('timeslot.durationInMin', Math.floor(d.computedDuration * multiplier));
        },
      );
    };

    // carrier is offered only from companies inheriting the timeslot module, so an assigned carrier outside that list stays unselected
    const prefillAssignedCarrier = (ac: AbortController, d: ExpeditionDetailV3OutDTO) => {
      if (!isOwner || !d.isCarrierAssigned || isNullOrUndefined(d.assignedCarrierId)) return;

      lovAllCompaniesByInheritModule(CompanyModuleEnum.TIMESLOTS, companies => {
        if (ac && ac.signal.aborted) return;
        if (companies.some(c => c.id === d.assignedCarrierId)) {
          form.setFieldValue('carrierId', d.assignedCarrierId);
        }
      });
    };

    const loadExpeditionOrders = (ac: AbortController) => {
      if (isNotNullOrUndefined(createTimeslotRequest.expeditionId)) {
        getExpeditionV3Detail(createTimeslotRequest.expeditionId, (d: ExpeditionDetailV3OutDTO) => {
          if (ac && ac.signal.aborted) {
            return;
          }
          setIsReservationOrderLinkedToExpOrder(d.isReservationOrderLinkedToExpOrder);

          form.setFieldValue('carPlate', d.carPlate);
          prefillAssignedCarrier(ac, d);

          const { orders: relevantOrders, sourceCount } = expeditionOrdersForReservation(d, createTimeslotRequest.expeditionLocationId);

          setLinkedExpeditionWithoutOrders(d.isReservationOrderLinkedToExpOrder === true && sourceCount === 0);

          if (showOrders === true) {
            if (relevantOrders.length === 0) {
              const requiredItems = isOwner ? settings?.ownerManipulationItemsRequired : settings?.counterPartyManipulationItemsRequired;
              form.setFieldValue('orders', [emptyOrder(requiredItems || false)]);
            } else {
              form.setFieldValue('orders', relevantOrders);
            }
          } else {
            form.setFieldValue('orders', null);
          }
        });
      }
    };

    // runs only after the settings arrived, otherwise both responses race and the slower one wins.
    // keyed on a boolean, not on the settings object - a repeated settings response (StrictMode fires the
    // request twice) would otherwise re-run this effect and abort the prefill that is already in flight
    const settingsLoaded = isNotNullOrUndefined(settings);
    useEffect(() => {
      const ac = new AbortController();
      if (isNullOrUndefined(initialData) && settingsLoaded) {
        loadExpeditionOrders(ac);
      }
      return () => {
        ac.abort();
      };
    }, [settingsLoaded]);

    // Load ramps when companyLocationId changes (needed for ramp picker)
    useEffect(() => {
      const ac = new AbortController();
      if (isNotNullOrUndefined(form.values.companyLocationId) && isNotNullOrUndefined(form.values.companyLocationCustomerId)) {
        getSharedRampsForCompanyLocation(
          form.values.companyLocationId,
          form.values.companyLocationCustomerId,
          false,
          (loadedRamps) => {
            if (!ac.signal.aborted) {
              setRamps(loadedRamps);
            }
          }
        );
      } else {
        setRamps([]);
      }
      return () => {
        ac.abort();
      };
    }, [form.values.companyLocationId, form.values.companyLocationCustomerId]);

    // Update timezoneId when rampId changes (ramp has priority over location)
    useEffect(() => {
      if (isNotNullOrUndefined(form.values.timeslot?.rampId) && ramps.length > 0) {
        const selectedRamp = ramps.find(r => r.rampId === form.values.timeslot.rampId);
        if (selectedRamp?.timezoneId && selectedRamp.timezoneId !== form.values.timeslot.timezoneId) {
          console.log('Updating timezoneId from selected ramp:', selectedRamp.timezoneId);
          form.setFieldValue('timeslot.timezoneId', selectedRamp.timezoneId);
        }
      }
    }, [form.values.timeslot?.rampId, ramps]);

    // Set timezoneId from location's first ramp only if not set (fallback for null from caller)
    useEffect(() => {
      if (isNullOrUndefined(form.values.timeslot?.rampId) &&
          isNullOrUndefined(form.values.timeslot?.timezoneId) &&
          ramps.length > 0) {
        const firstRampTimezone = ramps[0]?.timezoneId;
        if (firstRampTimezone) {
          console.log('Setting timezoneId from location first ramp (fallback):', firstRampTimezone);
          form.setFieldValue('timeslot.timezoneId', firstRampTimezone);
        }
      }
    }, [ramps, form.values.timeslot?.rampId, form.values.timeslot?.timezoneId]);

    const [apiRequest, setApiRequest] = useState(false);

    useEffect(() => {
      checkReservationHandler();
    }, [debouncedValue]);

    const isOwner = loggedUser.companyId === form?.values?.companyLocationCustomerId;
    const foldersRequired = isOwner && settings?.foldersRequired === true;

    // RD-2977: a single accessible folder is pre-selected when folders are required
    useEffect(() => {
      if (!foldersRequired) return;
      if (accessibleFolders.length === 1 && (form.values.folders?.length ?? 0) === 0) {
        form.setFieldValue(form.names.folders, [accessibleFolders[0]]);
      }
    }, [foldersRequired, accessibleFolders]);
    const canEditDuration = isOwner ? settings?.editDuration == true : settings?.editDurationCounterParty == true;
    const slotsRequired = isOwner ? settings?.slotsRequired == true : settings?.slotsRequiredCounterParty == true;
    const reservationActionTypeEnabled = isOwner ? settings?.ownerShowActionTypes == true : settings?.counterPartyShowActionTypes == true;
    const reservationActionTypeRequired =
      reservationActionTypeEnabled && (isOwner ? settings?.ownerActionTypeRequired == true : settings?.counterPartyActionTypeRequired == true);
    const driverNameRequired = isOwner ? settings?.driverNameRequired == true : settings?.driverNameRequiredCounterParty == true;
    const driverPhoneRequired = isOwner ? settings?.driverPhoneRequired == true : settings?.driverPhoneRequiredCounterParty == true;
    const driverIdRequired = isOwner ? settings?.driverIdRequired == true : settings?.driverIdRequiredCounterParty == true;
    const carPlateRequired = isOwner ? settings?.carPlateRequired == true : settings?.carPlateRequiredCounterParty == true;

    const createExpeditionEnabled =
      loggedUser?.features?.includes(FeaturesEnum.RES_DUPLICATION_WITH_EXP) &&
      Boolean(createTimeslotRequest?.expeditionId) &&
      Boolean(createTimeslotRequest?.reservationId);

    useEffect(() => {
      setTruckTypeEnabled(loggedUser?.features?.includes(FeaturesEnum.RESERVATION_TRUCK_TYPE) || settings?.truckTypeEnabled);
      setTruckTypeRequired(loggedUser?.features?.includes(FeaturesEnum.RESERVATION_TRUCK_TYPE) && settings?.truckTypeRequired);
    }, [loggedUser, settings]);

    useEffect(() => {
      if (
        settings?.externalOrdersApiType === ExternalOrdersApiTypeEnum.NOTINO_PAP &&
        isNotNullOrUndefined(form.values.companyLocationCustomerId) &&
        isNotNullOrUndefined(form.values.companyLocationId) &&
        isNotNullOrUndefined(form.values.carrierId)
      ) {
        getExternalOrders(form.values.companyLocationCustomerId, form.values.carrierId, form.values.companyLocationId, d => {
          setExternalOrdersResponse(d);
        });
      } else {
        setExternalOrdersResponse(undefined);
      }
    }, [settings?.externalOrdersApiType, form.values.companyLocationCustomerId, form.values.companyLocationId, form.values.carrierId]);

    const offerAvailableSlots = () => {
      if (form.values.timeslot.localDate && form.values.timeslot.durationInMin && form.values.companyLocationId) {
        getFreeSlotsForLocationReservation(
          form.values.timeslot.localDate,
          parseInt(form.values.timeslot.durationInMin),
          form.values.companyLocationId,
          [],
          {
            customerId: form.values.companyLocationCustomerId,
            rampId: null,
            locationId: form.values.companyLocationId,
            labelIds: [],
            orders: mapGlobalOrderWithItemsForCheckTimeslots(form.values.orders),
            linkToExpeditionLocationId: createTimeslotRequest.expeditionLocationId,
            openGate: false,
            createExpedition: false,
            flags: toEditableFlags(form.values.flags),
            sendNotification: true,
          },
          d => {
            setSlots(d);
          },
        );
      }
    };

    useEffect(() => {
      if (!form?.values) return;
      offerAvailableSlots();
    }, [form?.values?.timeslot?.localDate, form?.values?.timeslot?.durationInMin, form?.values?.companyLocationId]);

    const checkReservationHandler = () => {
      if (isNotNullOrUndefined(debouncedValue)) {
        setApiRequest(true);
        checkReservation(
          [],
          {
            customerId: form.values.companyLocationCustomerId,
            timeslot: timeslotForReservationCheck(debouncedValue),
            orders: mapGlobalOrderWithItemsForCheckTimeslots(form.values.orders),
            rampId: debouncedValue.rampId,
            locationId: form.values.companyLocationId,
            labelIds: [],
            linkToExpeditionLocationId: createTimeslotRequest.expeditionLocationId,
            openGate: false,
            createExpedition: false,
            flags: toEditableFlags(form.values.flags),
            sendNotification: true,
          },
          () => {
            setApiRequest(false);
            setReservationValid(true);
            setReservationCheckMessages([]);
          },
          null,
          {
            onError: e => {
              setApiRequest(false);
              setReservationValid(false);
              // Unknown/transport error — no messages, the generic wording is shown
              setReservationCheckMessages([]);
            },
            onValidationFailed: messages => {
              setApiRequest(false);
              setReservationValid(false);
              setReservationCheckMessages(messages);
            },
          },
        );
      }
    };

    const save = (data: CreateTimeslotForm) => {
      setLoading(true);
      createReservation(
        buildCreateReservationRequest({
          data,
          values: form.values,
          context: createTimeslotRequest,
          isReservationOrderLinkedToExpOrder,
          externalOrdersResponse,
          onBehalfOfEnabled,
        }),
        d => {
          setCreateTimeslotRequest(undefined);
          onContinue(d);
          setLoading(false);
        },
        null,
        {
          onValidationFailed: originalResponse => {
            setBeValidationErrors(originalResponse);
            setLoading(false);
          },
        },
      );
    };
    const applySlot = (slot: TimeslotCalendarDTO, rampId: string) => {
      console.log('applySlot', { slotTimezoneId: slot.timezoneId, rampId });

      form.setFieldValue('timeslot.arrivalHourString', stringHHmmFromDayjs(dayjsFromApiUtcInTz(slot.utcStart, slot.timezoneId)));
      form.setFieldValue('timeslot.rampId', rampId);
      form.setFieldValue('timeslot.timezoneId', slot.timezoneId);
      setSlots(undefined);
    };

    const handleExpeditionCheck = () => {
      if (createTimeslotRequest?.reservationId) {
        const orderNames = form.values.orders?.map(order => order.orderName) || [];
        checkReservationIfExpeditionCanBeCreated(
          {
            reservationOrderNames: orderNames,
            originalExpeditionId: createTimeslotRequest.expeditionId,
          },
          response => {
            setExpeditionLocations(response.locations);
            setCanCreateLinkedExpedition(!response.cannotBeCreated);
            setLinkedExpeditionError(checkOrderMismatch(form.values.orders, response.locations, tr));
            setExpeditionApplicationId(response.expeditionApplicationId);
          },
        );
      } else {
        setExpeditionLocations([]);
        setLinkedExpeditionError(null);
      }
    };

    useEffect(() => {
      if (form.values.createExpedition) {
        handleExpeditionCheck();
      }
    }, [form.values.orders]);

    useEffect(() => {
      setRequiredOrderItems(isOwner ? settings?.ownerManipulationItemsRequired : settings?.counterPartyManipulationItemsRequired || false);
    }, [loggedUser, form, settings]);

    useEffect(() => {
      if (!isNotNullOrUndefined(initialData) && isNotNullOrUndefined(form?.values?.orders) && form.values.orders.length > 0 && requiredOrderItems !== undefined) {
        const updatedOrders = form.values.orders.map(() => emptyOrder(requiredOrderItems));
        form.setFieldValue('orders', updatedOrders);
      }
    }, [requiredOrderItems]);

    const continueDisabled = form.values.createExpedition && !canCreateLinkedExpedition;
    const optionalDimensions = form?.values?.orders?.some(order =>
      order.items?.some(item =>
        item.height != null || item.width != null || item.length != null || item.weight != null
      )
    );
    const reservationTestIdPrefix = createTimeslotRequest?.testIdPrefix
      ? `${createTimeslotRequest.testIdPrefix}-expectedHandling-newReservation`
      : undefined;

    const buildReservationTestId = (...parts: Array<string | null | undefined>) => {
      if (!reservationTestIdPrefix) return undefined;
      return [reservationTestIdPrefix, ...parts.filter(Boolean)].join('-');
    };

    const buildOrderTestId = (orderIndex: number, suffix: string) => {
      const orderPrefix = buildReservationTestId(`order${orderIndex + 1}`);
      if (!orderPrefix) return undefined;

      switch (suffix) {
        case 'id':
          return `${orderPrefix}-orderName`;
        default:
          return `${orderPrefix}-${suffix}`;
      }
    };

    const buildManUnitTestId = (orderIndex: number, itemIndex: number, suffix?: string) => {
      const manUnitPrefix = buildReservationTestId(`order${orderIndex + 1}`, `manUnit${itemIndex + 1}`);
      if (!manUnitPrefix) return undefined;

      switch (suffix) {
        case undefined:
        case '':
          return manUnitPrefix;
        case 'Amount':
          return `${manUnitPrefix}-amount`;
        case 'stackable':
          return `${manUnitPrefix}-stackable`;
        case 'Length':
          return `${manUnitPrefix}-length`;
        case 'Width':
          return `${manUnitPrefix}-width`;
        case 'Height':
          return `${manUnitPrefix}-height`;
        case 'Weight':
          return `${manUnitPrefix}-weight`;
        default:
          return `${manUnitPrefix}-${suffix}`;
      }
    };

    const buildPlanSlotTestId = (rampName: string, since: string, till: string) => {
      return buildReservationTestId('plan', toTestIdSlug(rampName), `${toCompactTimePart(since)}-${toCompactTimePart(till)}`);
    };

    const { externalOrdersMinDate, externalOrdersMaxDate } = useExternalOrdersDateRange(form.values.orders);

    useEffect(() => {
      if (!externalOrdersMinDate) return;
      const current = form.values.timeslot?.localDate;
      if (!current) return;
      const minStr = dayjs(externalOrdersMinDate).format('YYYY-MM-DD');
      const maxStr = externalOrdersMaxDate ? dayjs(externalOrdersMaxDate).format('YYYY-MM-DD') : null;
      if (current < minStr || (maxStr && current > maxStr)) {
        form.setFieldValue('timeslot.localDate', minStr);
      }
    }, [externalOrdersMinDate, externalOrdersMaxDate]);

    return (
      <>
        <RingilSidebar
          visible={isNotNullOrUndefined(createTimeslotRequest)}
          onBack={() => setCreateTimeslotRequest(null)}
          onContinue={() => {
            form.validateAndSend();
          }}
          heading={
            <RowWithSpaceBetween>
              <div>{tr(`CreateTimeslotSidebar.heading`, `Create timeslot`)}</div>
              <ErrorCountBadge errorCount={new Set(form.validationErrors.map(t => t.path)).size} />
            </RowWithSpaceBetween>
          }
          disableSaveButton={continueDisabled}
          loading={loading}
          dataTestIdContinue={buildReservationTestId('save')}
          dataTestIdBack={buildReservationTestId('close')}
        >
          <BackendValidationComponent />
          <ColWithGap>
            {createTimeslotRequest?.applicationId && (
              <VerticalFieldAndValueWithIcon
                label={tr(`CreateTimeslotSidebar.forExpedition`, `For expedition`)}
                valueText={createTimeslotRequest?.applicationId}
                iconPath={'/images/icons/ringil3/forExpedition.svg'}
              />
            )}
            <TwoColGrid>
              <Field label={tr(`CreateTimeslotSidebar.customer`, `Customer`)} readOnly={headerDisabled}>
                <InputEntityOwner
                  companyId={form.values.companyLocationCustomerId}
                  onChange={v => {
                    form.setFieldValue(form.names.companyLocationCustomerId, v);
                  }}
                  moduleType={CompanyModuleEnum.TIMESLOTS}
                  onChangeCompanyCode={v => {
                    form.setFieldValue(form.names.customerCompanyCode, v);
                  }}
                  readonly={headerDisabled}
                  style={{ maxWidth: '100%' }}
                />
              </Field>
              <Field
                required={isNullOrUndefined(form.values.companyLocationId)}
                label={tr(`CreateTimeslotSidebar.location`, `Location`)}
                errorMessage={findErrorMessage(form.validationErrors, `companyLocationId`, tr)}
                readOnly={headerDisabled}
              >
                <InputCompanyLocation
                  companyId={form.values.companyLocationCustomerId}
                  companyLocationId={form.values.companyLocationId}
                  moduleName={CompanyModuleEnum.TIMESLOTS}
                  disabled={headerDisabled}
                  dataTestId={buildReservationTestId('location')}
                  onChange={v => {
                    form.setFieldValue(form.names.companyLocationId, v?.id);
                  }}
                  showOnlyWithRamp={true}
                  filterEnabled={true}
                  style={{ maxWidth: '100%' }}
                />
              </Field>
            </TwoColGrid>
            {truckTypeEnabled && (
              <Field
                required={truckTypeRequired}
                label={tr(`CreateTimeslotSidebar.truckType`, `Truck type`)}
                errorMessage={findErrorMessage(form.validationErrors, `truckType`, tr)}
              >
                <Dropdown
                  options={truckTypes}
                  value={form.values.truckType}
                  optionLabel={'name'}
                  optionValue={'code'}
                  onChange={e => form.setFieldValue(form.names.truckType, e.value)}
                  showClear={true}
                  placeholder={tr('CreateTimeslotSidebar.selectTruckType', 'Select type of truck')}
                />
              </Field>
            )}

            {onBehalfOfEnabled && (
              <Field
                label={tr(`CreateTimeslotSidebar.onBehalfOf`, `On behalf of`)}
                errorMessage={findErrorMessage(form.validationErrors, `onBehalfOfCodeListId`, tr)}
              >
                <Dropdown
                  value={form.values.onBehalfOfCodeListId}
                  options={onBehalfOfOptions}
                  onChange={e => form.setFieldValue(form.names.onBehalfOfCodeListId, e.value)}
                  optionLabel={'name'}
                  optionValue={'id'}
                  itemTemplate={(option: LovCompanyCodeListDTO) => <div>{formatCompanyCodeListLabel(option)}</div>}
                  valueTemplate={(option: LovCompanyCodeListDTO) =>
                    option ? (
                      <span>{formatCompanyCodeListLabel(option)}</span>
                    ) : (
                      <span>{tr('CreateTimeslotSidebar.onBehalfOfPlaceholder', 'Select on behalf of')}</span>
                    )
                  }
                  showClear
                  filter
                  filterBy="name,externalId"
                  placeholder={tr('CreateTimeslotSidebar.onBehalfOfPlaceholder', 'Select on behalf of')}
                />
              </Field>
            )}

            {showNameAndNote === true && (
              <>
                <HorizontalLine variant={'tertiary'} />
                <Field label={tr('CreateTimeslotSidebar.reservationName', 'Reservation name')} readOnly={false}>
                  <RingilTextArea
                    rows={1}
                    value={form.values.reservationName}
                    onChange={v => form.setFieldValue(form.names.reservationName, v)}
                    maxLength={255}
                    className={'resize-none'}
                    dataTestId={buildReservationTestId('reservationName')}
                  />
                </Field>
                <InputNoteWithLink
                  note={form.values.reservationNote}
                  onChanges={v => {
                    form.setFieldValue(form.names.reservationNote, v);
                  }}
                  noteLabel={tr(`CreateTimeslotSidebar.reservationNote`, `Reservation note`)}
                  dataTestIdButton={buildReservationTestId('note')}
                  dataTestIdInput={buildReservationTestId('noteInput')}
                  maxLength={4000}
                />
              </>
            )}

            <COHeadline variant={'h3'} title={tr(`CreateTimeslotSidebar.cargo`, `Cargo`)}></COHeadline>
            {reservationActionTypeEnabled && (
              <Field
                label={tr(`CreateTimeslotSidebar.manipulationType`, `Manipulation type`)}
                errorMessage={findErrorMessage(form.validationErrors, 'actionTypes', tr)}
              >
                <InputManipulationType
                  mandatory={reservationActionTypeRequired}
                  loadingTypes={form.values.actionTypes}
                  onChange={v => {
                    form.setFieldValue(form.names.actionTypes, v);
                  }}
                  dataTestIdPrefix={buildReservationTestId('manipulationType')}
                />
              </Field>
            )}
            <ReservationFlagsField
              value={form.values.flags ?? []}
              onChange={next => form.setFieldValue(form.names.flags, next)}
              flagsConfig={flagsConfig}
              flagsErrorMessage={findErrorMessage(form.validationErrors, 'flags', tr)}
              unitsErrorMessage={idx => findErrorMessage(form.validationErrors, `flags[${idx}].units`, tr)}
              orders={showOrders === true ? form.values.orders : undefined}
              dataTestIdPrefix={reservationTestIdPrefix}
            />
            {showOrders === true && linkedExpeditionWithoutOrders && (
              <COInlineMessage variant="warning" icon={<SvgWarningExclamation />}>
                {linkedExpeditionWithoutOrdersMessage(tr)}
              </COInlineMessage>
            )}
            {showOrders === true && !linkedExpeditionWithoutOrders && (
              <>
                <Tabs
                  tabs={form.values.orders.map((_, oIndex) => ({
                    label: `${oIndex + 1}.`,
                    active: activeTab === oIndex,
                    anchorId: `anchor_order_${oIndex}`,
                  }))}
                  onTabChange={setActiveTab}
                  tailSpacer={false}
                />
                <ColOdd>
                  <OrdersComponent
                    orderTitle={tr('OrdersComponent.orderReference', 'Order/Reference')}
                    orders={form.values.orders}
                    companyId={form.values.companyLocationCustomerId}
                    onChange={v =>
                      form.useProduce(d => {
                        d.orders = v;
                      })
                    }
                    config={generateTimeslotOrderConfig(requiredOrderItems, optionalDimensions ? 'optional' : 'never')}
                    validationErrors={form.validationErrors}
                    isReservationOrderLinkedToExpOrder={isReservationOrderLinkedToExpOrder}
                    buildOrderTestId={buildOrderTestId}
                    buildManUnitTestId={buildManUnitTestId}
                    externalOrders={externalOrdersResponse?.orders}
                    externalIdPresent={isExternalIdPresent(externalOrdersResponse)}
                  />
                </ColOdd>
              </>
            )}

            <HorizontalLine variant={'tertiary'} />
            <COHeadline variant={'h3'} title={tr(`CreateTimeslotSidebar.plan`, `Plan`)}></COHeadline>
            <VerticalFieldAndValue
              label={tr(`CreateTimeslotSidebar.localDate`, `Date`)}
              labelMode={'normal'}
              errorMessage={findErrorMessage(form.validationErrors, 'timeslot.localDate', tr)}
            >
              <RingilCalendar
                value={form.values.timeslot.localDate}
                onChange={v => form.setFieldValue('timeslot.localDate', v)}
                fullWidth
                minDate={externalOrdersMinDate}
                maxDate={externalOrdersMaxDate}
                dataTestId={buildReservationTestId('plan', 'date')}
              />
            </VerticalFieldAndValue>
            {!slots && (
              <LinkWithIcon
                icon={<SvgAvailableDates />}
                label={tr(`CreateTimeslotSidebar.offerAvailableSlots`, `Offer available slots`)}
                onClick={() => {
                  offerAvailableSlots();
                }}
                dataTestId={buildReservationTestId('plan', 'offerAvailableSlots')}
              />
            )}

            {slots && (
              <Panel onClose={() => setSlots(undefined)} headingText={tr('CreateTimeslotSidebar.availableSlots', 'Available slots')} closeIcon={<SvgClose />}>
                <ColWithGap>
                  {!slots[0] && (
                    <VerticalFieldAndValue labelMode={'normal'} >
                        <NoAvailableSlotsMessage />
                    </VerticalFieldAndValue>
                  )}
                  {slots[0]?.freeSlots?.map((s, i) => {
                    return (
                      <VerticalFieldAndValue label={s.name} labelMode={'normal'} key={i}>
                        {s.slots.length == 0 && (
                          <NoAvailableSlotsMessage unavailability={s.unavailability} />
                        )}
                        <SlotsGrid>
                          {s.slots.map((slot, j) => {
                            const slotTestId = buildPlanSlotTestId(s.name, stringHHmmFromStringInTz(slot.utcStart, slot.timezoneId), stringHHmmFromStringInTz(slot.utcEnd, slot.timezoneId));
                            return (
                              <ClickableCursor key={j} onClick={() => applySlot(slot, s.rampId)} data-testid={slotTestId}>
                                <COSelect text={`${stringHHmmFromStringInTz(slot.utcStart, slot.timezoneId)} - ${stringHHmmFromStringInTz(slot.utcEnd, slot.timezoneId)}`} />
                              </ClickableCursor>
                            );
                          })}
                        </SlotsGrid>
                      </VerticalFieldAndValue>
                    );
                  })}
                </ColWithGap>
              </Panel>
            )}
            <TwoColGrid>
              <Field
                label={tr(`CreateTimeslotSidebar.ramp`, `Ramp`)}
                required={slotsRequired}
                errorMessage={findErrorMessage(form.validationErrors, 'timeslot.rampId', tr)}
              >
                <InputCompanyRamp
                  value={form.values.timeslot.rampId}
                  onChange={v => form.setFieldValue('timeslot.rampId', v)}
                  companyLocationId={form.values.companyLocationId}
                  companyId={form.values.companyLocationCustomerId}
                  showTemporaryClosed={false}
                  dataTestId={buildReservationTestId('plan', 'ramp')}
                />
              </Field>
              <Field
                label={tr(`CreateTimeslotSidebar.duration`, `Duration`)}
                required={slotsRequired}
                readOnly={!canEditDuration}
                errorMessage={findErrorMessage(form.validationErrors, 'timeslot.durationInMin', tr)}
              >
                <InputTextWithSuffix
                  suffix={'min'}
                  fullWidth={true}
                  value={form.values.timeslot.durationInMin}
                  onChange={v => form.setFieldValue('timeslot.durationInMin', parseNumberOrUndefined(v))}
                  readOnly={!canEditDuration}
                  dataTestId={buildReservationTestId('plan', 'reservationLength')}
                />
              </Field>
            </TwoColGrid>
            <Field
              label={tr(`CreateTimeslotSidebar.arrival`, `Arrival`)}
              required={slotsRequired}
              errorMessage={findErrorMessage(form.validationErrors, 'timeslot.arrivalHourString', tr)}
            >
              <InputHourManual
                value={form.values.timeslot.arrivalHourString}
                onChange={v => form.setFieldValue('timeslot.arrivalHourString', v)}
                dataTestId={buildReservationTestId('plan', 'timeslotStart')}
              />
            </Field>
            <RowWithGap>
              <LinkWithIcon
                icon={<SvgRefresh />}
                label={tr(`CreateTimeslotSidebar.checkReservation`, `Check reservation`)}
                onClick={() => {
                  checkReservationHandler();
                }}
                dataTestId={buildReservationTestId('plan', 'checkReservation')}
              />

              {apiRequest && (
                <>
                  <ProgressSpinner
                    style={{
                      width: '15px',
                      height: '15px',
                      marginRight: '0.5rem',
                      color: 'var(--ringil3-brand-color)',
                    }}
                    strokeWidth="3"
                    animationDuration="1s"
                  />
                </>
              )}
            </RowWithGap>
            <ReservationCheckResult valid={reservationValid} messages={reservationCheckMessages} />
            <>
              <HorizontalLine variant={'tertiary'} />
              <COHeadline variant={'h3'} title={tr(`CreateTimeslotSidebar.carrier`, `Carrier`)}></COHeadline>
              <Field label={tr(`CreateTimeslotSidebar.carrier`, `Carrier`)}>
                <InputInheriterCompanyRES
                  companyId={form.values.companyLocationCustomerId}
                  inheriterId={form.values.carrierId}
                  onChange={v => form.setFieldValue(form.names.carrierId, v)}
                  locationId={createTimeslotRequest.expeditionLocationId}
                  module={CompanyModuleEnum.TIMESLOTS}
                  showClear={isOwner}
                  readonly={!isOwner}
                  dataTestId={buildReservationTestId('carrier')}
                />
              </Field>

              <Field
                label={tr(`CreateTimeslotSidebar.driver`, `Driver`)}
                required={driverNameRequired}
                errorMessage={findErrorMessage(form.validationErrors, `driverInfo.driverName`, tr)}
              >
                <RingilTextArea
                  rows={1}
                  value={form.values.driverInfo.driverName}
                  onChange={v => form.setFieldValue('driverInfo.driverName', v)}
                  maxLength={255}
                  className={'resize-none'}
                  dataTestId={buildReservationTestId('carrier', 'driver')}
                />
              </Field>

              <Field
                label={tr(`CreateTimeslotSidebar.driverPhone`, `Driver phone`)}
                required={driverPhoneRequired}
                errorMessage={findErrorMessage(form.validationErrors, `driverInfo.driverPhone`, tr)}
              >
                <RingilTextArea
                  rows={1}
                  value={form.values.driverInfo.driverPhone}
                  onChange={v => form.setFieldValue('driverInfo.driverPhone', v)}
                  maxLength={255}
                  className={'resize-none'}
                  dataTestId={buildReservationTestId('carrier', 'driverPhone')}
                />
              </Field>

              <Field
                label={tr(`CreateTimeslotSidebar.driverId`, `Driver ID`)}
                required={driverIdRequired}
                errorMessage={findErrorMessage(form.validationErrors, `driverInfo.driverId`, tr)}
              >
                <RingilTextArea
                  rows={1}
                  value={form.values.driverInfo.driverId}
                  onChange={v => form.setFieldValue('driverInfo.driverId', v)}
                  maxLength={255}
                  className={'resize-none'}
                  dataTestId={buildReservationTestId('carrier', 'driverId')}
                />
              </Field>

              <Field
                label={tr(`CreateTimeslotSidebar.carPlate`, `Car plate`)}
                required={carPlateRequired}
                errorMessage={findErrorMessage(form.validationErrors, `carPlate`, tr)}
              >
                <RingilTextArea
                  rows={1}
                  value={form.values.carPlate}
                  onChange={v => form.setFieldValue(form.names.carPlate, v)}
                  maxLength={255}
                  className={'resize-none'}
                  dataTestId={buildReservationTestId('carrier', 'carPlate')}
                />
              </Field>

              <InputNoteWithLink
                note={form.values.carrierNote}
                onChanges={v => {
                  form.setFieldValue(form.names.carrierNote, v);
                }}
                noteLabel={tr(`CreateTimeslotSidebar.carrierNote`, `Carrier note`)}
                dataTestIdButton={buildReservationTestId('carrier', 'carrierNote')}
                dataTestIdInput={buildReservationTestId('carrier', 'carrierNoteInput')}
                maxLength={4000}
              />

              {createExpeditionEnabled && (
                <>
                  <HorizontalLine variant={'tertiary'} />
                  <RingilInputSwitch
                    value={form.values.createExpedition}
                    onChange={(checked: boolean) => {
                      form.setFieldValue('createExpedition', checked);
                      handleExpeditionCheck();
                    }}
                    label={tr(`CreateTimeslotSidebar.createExpedition`, `Create corresponding expedition`)}
                  />

                  {form.values.createExpedition && expeditionLocations.length > 0 && (
                    <>
                      <VerticalFieldAndValueWithIcon
                        label={tr('CreateTimeslotSidebar.expeditionApplicationId', 'Original expedition')}
                        valueText={expeditionApplicationId}
                        icon={<SvgForExpedition />}
                      />
                      {linkedExpeditionError && (
                        <COInlineMessage variant="warning" icon={<SvgWarningExclamation />}>
                          {linkedExpeditionError}
                        </COInlineMessage>
                      )}
                      <div className="mt-4">
                        <COHeadline variant={'h3'} title={tr(`CreateTimeslotSidebar.expeditionLocations`, `Expedition Locations`)}></COHeadline>
                        {expeditionLocations.map((location, index) => {
                          const formOrderNames = location.orderNames.filter(orderName => form.values.orders?.some(order => order.orderName === orderName));

                          return (
                            <div key={location.id} className="mt-2">
                              <VerticalFieldAndValue
                                label={tr(`CreateTimeslotSidebar.location`, `Location`) + ` ${index + 1}`}
                                labelMode={'normal'}
                                readOnly={formOrderNames.length === 0}
                              >
                                <InputText
                                  value={location.name}
                                  disabled={formOrderNames.length === 0}
                                  readOnly={true}
                                  style={{
                                    border: '1px solid var(--Content-Quaternary, #E7EBEE)',
                                    background: 'var(--Base-BaseTertiary, #F7F7F7)',
                                  }}
                                />
                                {formOrderNames.length > 0 && (
                                  <OrderLocationInfo>
                                    {tr(`CreateTimeslotSidebar.locationOrders`, `Orders`)}: {formOrderNames.join(', ')}
                                  </OrderLocationInfo>
                                )}
                                {formOrderNames.length === 0 && (
                                  <OrderLocationInfo>{tr(`CreateTimeslotSidebar.locationWithNoOrders`, `No orders for location`)}</OrderLocationInfo>
                                )}
                              </VerticalFieldAndValue>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}

              {showCamera === true && (
                <>
                  <HorizontalLine variant={'tertiary'} />
                  <COHeadline variant={'h3'} title={tr('CreateTimeslotSidebar.kamery', 'Kamery')}></COHeadline>
                  <InputCheckbox
                    label={tr('CreateTimeslotSidebar.poVytvoreniRezervaceOtevritZavoruNavazanouNaKameru', 'Po vytvoření rezervace otevřít závoru navázanou na kameru')}
                    value={form.values.openGate}
                    onChange={(_, v) => {
                      form.useProduce(d => {
                        d.openGate = v;
                      });
                    }}
                  />

                  <div className={'flex flex-col gap-2'}>
                    <InputCamera
                      cameraId={form.values.cameraId}
                      onChange={v =>
                        form.useProduce(d => {
                          d.cameraId = v;
                        })
                      }
                      companyLocationId={form.values.companyLocationId}
                    />

                    <ValidationDiv errors={form.validationErrors} path={'cameraId'} />
                  </div>
                </>
              )}

              {/* RD-2977: folders of the owner company - only when the user may file into at least one */}
              {isOwner && accessibleFolders.length > 0 && (
                <>
                  <HorizontalLine variant={'tertiary'} />
                  <Field label={tr('CreateTimeslotSidebar.folders', 'Složky')} required={foldersRequired} errorMessage={findValidationMessage(form.validationErrors, 'folders', tr)}>
                    <InputFolders
                      folders={form.values.folders ?? []}
                      options={accessibleFolders}
                      onChange={v => form.setFieldValue(form.names.folders, v)}
                      entityType={EntityTypeEnum.TIMESLOT}
                      dataTestIdBase={'createTimeslot-folders'}
                    />
                  </Field>
                </>
              )}

              <InputCheckbox
                label={tr('CreateTimeslotSidebar.sendNotification', 'Send notification email')}
                value={form.values.sendNotification}
                onChange={(_, v) => {
                  form.useProduce(d => {
                    d.sendNotification = v;
                  });
                }}
              />
            </>
          </ColWithGap>
        </RingilSidebar>
      </>
    );
  };

  return {
    CreateTimeslotSidebar,
    setCreateTimeslotRequest,
    createTimeslotRequest,
  };
};

const ButtonDownWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const SlotsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
`;

const OrderLocationInfo = styled.div`
  color: var(--Content-Secondary, #616061);
  font-family: 'DM Sans';
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  margin-top: 4px;
`;
